import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, FilePlus2, FolderLock, Inbox, Loader2, Lock, Search, ShieldAlert } from "lucide-react";
import { api, errText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useAdmin } from "@/context/AdminContext";
import { CaseFile } from "./CaseFile";
import { OpenCaseDialog } from "./OpenCaseDialog";
import { ReportsQueue } from "./ReportsQueue";

export const TYPE_TONE = {
  security: "bg-[#7F1D1D] text-white", account: "bg-[#1E3A8A] text-white", conduct: "bg-[#78350F] text-white",
  complaint: "bg-[#14532D] text-white", other: "bg-[#3F3F46] text-white",
};
export const STATUS_TONE = { open: "border-[#0A0A0A] text-[#0A0A0A]", investigating: "border-[#B45309] text-[#B45309]", awaiting_info: "border-[#1D4ED8] text-[#1D4ED8]", closed: "border-[#57534E] bg-[#E7E5E4] text-[#3a3836]" };

export const TypeTag = ({ c }) => <span className={`inline-block px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${TYPE_TONE[c.type] || TYPE_TONE.other}`}>{c.type_label}</span>;
export const StatusTag = ({ c }) => <span className={`inline-block border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${STATUS_TONE[c.status] || ""}`} data-testid={`case-status-${c.ref}`}>{c.status_label}</span>;

const INBOXES = [
  ["waiting", "Waiting to be opened", "New complaints and reports", Inbox, true],
  ["mine", "My inbox", "Open cases assigned to you", FolderLock, false],
  ["closed", "Closed", "Locked · only you can open them", Archive, true],
];

const InboxTabs = ({ tab, setTab, counts, isAdmin }) => (
  <div className="mt-6 grid gap-2 sm:grid-cols-3" role="tablist" data-testid="case-inboxes">
    {INBOXES.filter(([, , , , ownerOnly]) => isAdmin || !ownerOnly).map(([k, l, d, Icon]) => (
      <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
        className={`group flex items-center gap-3 border-2 p-4 text-left transition-colors ${tab === k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-black/15 bg-white hover:border-[#0A0A0A]"}`} data-testid={`case-inbox-${k}`}>
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center ${tab === k ? "bg-white text-[#0A0A0A]" : "bg-[#F3F2EF] text-[#0A0A0A]"}`}>
          {k === "closed" ? <Lock className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-extrabold uppercase tracking-wider">{l}</span>
          <span className={`block text-xs ${tab === k ? "text-zinc-300" : "text-[#57534E]"}`}>{d}</span>
        </span>
        {counts[k] > 0 && <span className={`min-w-[26px] px-1.5 py-0.5 text-center text-xs font-bold ${tab === k ? "bg-white text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"}`} data-testid={`case-inbox-${k}-count`}>{counts[k]}</span>}
      </button>
    ))}
  </div>
);

const Register = ({ items, openId, onOpen }) => {
  const wide = !openId;
  const cols = wide ? "md:grid-cols-[110px_1fr_150px_120px] md:items-center md:gap-4" : "";
  return (
  <div className="border border-[#0A0A0A]/80 bg-white" data-testid="case-register">
    {wide && <div className="hidden grid-cols-[110px_1fr_150px_120px] gap-4 border-b border-[#0A0A0A]/80 bg-[#0A0A0A] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white md:grid">
      <span>Reference</span><span>Case</span><span>Handler</span><span>Updated</span>
    </div>}
    {!items ? <div className="h-32 animate-pulse bg-black/5" /> : !items.length ? (
      <p className="flex items-center justify-center gap-2 p-10 text-sm text-[#57534E]" data-testid="case-register-empty"><FolderLock className="h-4 w-4" />No cases here.</p>
    ) : (
      <ul className="divide-y divide-black/10">
        {items.map((c) => (
          <li key={c.id}>
            <button onClick={() => onOpen(c.id)} className={`grid w-full gap-1 px-4 py-3.5 text-left transition-colors ${cols} ${openId === c.id ? "bg-[#F3F2EF] shadow-[inset_4px_0_0_#E62314]" : "hover:bg-[#FAFAF9]"}`} data-testid={`case-row-${c.ref}`}>
              <span className="font-mono text-xs font-bold tracking-wider">{c.ref}</span>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-1.5"><TypeTag c={c} /><StatusTag c={c} />{c.suspended && <span className="text-[11px] font-bold uppercase tracking-wider text-[#C21B0E]">Account suspended</span>}</span>
                <span className="mt-1 block break-words text-sm font-semibold">{c.title}</span>
                {c.subject_name && <span className="block text-xs text-[#57534E]">About {c.subject_name}</span>}
              </span>
              <span className="text-xs text-[#3a3836]"><span className={wide ? "md:hidden" : ""}>Handler: </span>{c.handler_name}</span>
              <span className="text-xs text-[#57534E]">{fmtDateTime(c.updated_at)}</span>
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
  );
};

const AREA_COPY = {
  complaints: ["Confidential complaint cases", "Complaints from the public, staff conduct and other matters."],
  security: ["Account security cases", "Investigations into account security, such as unauthorised email changes and data breaches."],
};

export const CasesHub = ({ area = "complaints" }) => {
  const { isAdmin } = useAdmin();
  const [tab, setTabRaw] = useState(isAdmin ? "waiting" : "mine");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [closedItems, setClosedItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [reports, setReports] = useState([]);
  const closeFile = useCallback(() => setOpenId(null), []);
  const setTab = (k) => { setTabRaw(k); setOpenId(null); };
  const loadReports = useCallback(() => {
    if (isAdmin) api.get("/cases/reports", { params: { area } }).then((r) => setReports(Array.isArray(r.data) ? r.data : [])).catch(() => setReports([]));
    window.dispatchEvent(new Event("cm-reports"));
  }, [isAdmin, area]);
  useEffect(() => { loadReports(); return undefined; }, [loadReports]);
  const load = useCallback(() => {
    api.get("/cases", { params: { scope: "mine", area } }).then((r) => setItems(Array.isArray(r.data) ? r.data.filter((c) => c?.status !== "closed") : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
    if (isAdmin) api.get("/cases", { params: { scope: "all", area } }).then((r) => setClosedItems(Array.isArray(r.data) ? r.data.filter((c) => c?.status === "closed") : [])).catch(() => setClosedItems([]));
  }, [isAdmin, area]);
  useEffect(() => { load(); return undefined; }, [load]);
  useEffect(() => { api.get("/cases/meta", { params: { area } }).then((r) => setMeta(r.data)).catch(() => {}); return undefined; }, [area]);
  const term = q.trim().toLowerCase();
  const source = tab === "closed" ? closedItems : items;
  const shown = source && source.filter((c) => !term || [c.ref, c.title, c.subject_name, c.handler_name].some((v) => (v || "").toLowerCase().includes(term)));
  const counts = { waiting: reports.filter((r) => r.status === "waiting").length, mine: (items || []).length, closed: 0 };
  return (
    <div data-testid="cases-hub">
      <header className="flex flex-col gap-4 border-b-4 border-[#0A0A0A] pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#C21B0E]"><ShieldAlert className="h-4 w-4" />{AREA_COPY[area][0]}</p>
          <p className="mt-2 max-w-2xl text-sm text-[#57534E]">{AREA_COPY[area][1]} Only the handler{isAdmin ? " and you" : " and the owner"} can open a case. The person a case is about can never see it.</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex shrink-0 items-center justify-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#E62314]" data-testid="case-open-new"><FilePlus2 className="h-4 w-4" />Open a case</button>
      </header>
      <InboxTabs tab={tab} setTab={setTab} counts={counts} isAdmin={isAdmin} />
      {tab === "waiting" && isAdmin ? <ReportsQueue reports={reports} meta={meta} onChanged={loadReports} onCaseOpened={(c) => { load(); setTabRaw("mine"); setOpenId(c.id); }} /> : <>
      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#57534E]">{tab === "closed" ? <><Lock className="h-3.5 w-3.5" />Closed cases are locked and can't be changed</> : "Cases you're handling"}</p>
        <label className="flex items-center gap-2 border border-black/20 bg-white px-3 focus-within:border-black lg:w-72">
          <Search className="h-4 w-4 shrink-0 text-[#57534E]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, name or title" className="w-full bg-transparent py-2 text-sm outline-none" data-testid="case-search" />
        </label>
      </div>
      <div className={`mt-5 grid gap-6 ${openId ? "xl:grid-cols-[340px_minmax(0,1fr)]" : ""}`}>
        <div className={openId ? "hidden xl:block" : ""}><Register items={shown} openId={openId} onOpen={setOpenId} /></div>
        {openId && <CaseFile key={openId} id={openId} meta={meta} onClose={closeFile} onChanged={load} />}
      </div>
      </>}
      {!meta && creating && <p className="mt-3 flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Loading...</p>}
      <OpenCaseDialog open={creating && !!meta} meta={meta} onOpenChange={setCreating} onCreated={(c) => { load(); setTabRaw("mine"); setOpenId(c.id); }} />
    </div>
  );
};
